import type { ComponentType } from 'react';
import { ServiceDetailPage } from '@/components/ServiceDetailPage';

type ServicePageConfig = {
  path: string;
  navLabel: string;
  cardTitle: string;
  title: string;
  description: string;
  intro: string;
  highlights: string[];
  process: string[];
  idealFor: string[];
  seoPoints: string[];
  faqs: Array<{ question: string; answer: string }>;
};

function createServicePage(config: ServicePageConfig): ComponentType {
  return function ServicePage() {
    return (
      <ServiceDetailPage
        activePath={config.path}
        title={config.title}
        description={config.description}
        intro={config.intro}
        highlights={config.highlights}
        process={config.process}
        idealFor={config.idealFor}
        seoPoints={config.seoPoints}
        faqs={config.faqs}
      />
    );
  };
}

export const servicePageConfigs: ServicePageConfig[] = [
  {
    path: '/services/house-removals',
    navLabel: 'House Removals',
    cardTitle: 'House Removals',
    title: 'Professional House Removals & Home Moving Services | VIP House Movers',
    description: 'Expert house removals & home moving services in Wolverhampton & West Midlands. Professional home relocation, furniture moving, packing & more. Fully insured. Free quote today.',
    intro: 'VIP House Movers specialises in professional house removals and home moving services for families, homeowners, and busy households throughout Wolverhampton, Birmingham, Walsall, Dudley, and across the UK. Whether you\'re moving house locally or relocating nationally, our experienced team plans every removal with care, clear communication, and the right vehicle size so your home move feels organised from the first call to the final item being placed in your new home.',
    highlights: [
      'Professional house moving support for full property relocations and local domestic moves',
      'Home removals with protective wrapping, secure loading, transport, and careful unloading',
      'Flexible moving day scheduling and support for key handovers and property access needs',
      'Experienced movers and relocation specialists who treat your belongings with respect',
      'Fully insured house removals with transparent pricing and no hidden fees',
    ],
    process: [
      'Initial consultation to understand your property size, moving date, access details, and specific removal requirements.',
      'Our professional team arrives on time, assesses the property, protects key furniture and high-traffic areas, and loads efficiently.',
      'Your household items are transported securely in a suitable removal van to your new home or property.',
      'Careful unloading, furniture placement in correct rooms, and assembly support to help you settle in immediately.',
    ],
    idealFor: [
      'Families moving between homes within Wolverhampton or across the UK',
      'Homeowners seeking a managed and dependable professional removal team',
      'Property movers with large furniture, fragile belongings, or tight moving schedules',
      'Renters and first-time buyers needing comprehensive moving house solutions',
    ],
    seoPoints: [
      'Popular for 2-bedroom, 3-bedroom, and 4+ bedroom house removals across Wolverhampton and the West Midlands.',
      'Ideal when you need packing, loading, secure transport, unloading, and furniture placement as one complete service.',
      'Trusted by customers searching for fully insured house removals companies with flexible booking and transparent pricing.',
      'Suitable for local moves, regional relocations, cross-country moves, and nationwide UK home removals.',
    ],
    faqs: [
      {
        question: 'How much do house removals cost in Wolverhampton and the West Midlands?',
        answer: 'House removal costs depend on your property size, moving distance, and services needed (packing, storage, furniture assembly). We provide free, transparent quotes with no hidden fees. Typical costs range from £500 for small flat moves to £2,500+ for large family homes. Contact us for a personalised moving house quote.',
      },
      {
        question: 'Do you handle full house removals and smaller domestic moves?',
        answer: 'Yes, VIP House Movers supports everything from smaller flat removals and studio apartment moves to complete full family house relocations, with team size and vehicle capacity matched to your specific removal requirements.',
      },
      {
        question: 'Can you help with packing and furniture assembly for a house move?',
        answer: 'Absolutely. We offer full packing services using high-quality protective materials, professional furniture dismantling before transport, and careful reassembly in your new home to make your moving house experience much easier.',
      },
      {
        question: 'How far in advance should I book my house removal?',
        answer: 'We recommend booking 2-4 weeks in advance to secure your preferred moving date, especially for Fridays and end-of-month moves. However, we frequently accommodate last-minute and short-notice house removals based on availability.',
      },
      {
        question: 'Are house removals fully insured with VIP House Movers?',
        answer: 'Yes, all professional house removals are fully insured with comprehensive goods-in-transit and public liability insurance. Your belongings are fully protected from collection to delivery.',
      },
      {
        question: 'Do you offer moving house tips and advice?',
        answer: 'Yes, we provide expert moving house advice including packing tips, moving checklists, and guidance on preparing for your home removal to reduce stress and ensure a smooth transition.',
      },
    ],
  },
  {
    path: '/services/office-removals',
    navLabel: 'Office Removals',
    cardTitle: 'Office Removals',
    title: 'Office Removals for Businesses of All Sizes',
    description: 'Efficient office removals from VIP House Movers designed to reduce downtime and keep your business moving with minimal disruption.',
    intro: 'We help businesses relocate workspaces, equipment, furniture, and archived materials with a structured plan that protects productivity and keeps your office move on schedule.',
    highlights: [
      'Careful handling for office furniture, IT equipment, and boxed documents',
      'Flexible scheduling available for early mornings, evenings, and weekends',
      'Clear communication for property access, loading areas, and delivery times',
      'Professional crews focused on organisation, speed, and care',
    ],
    process: [
      'We review your office size, deadlines, and access requirements to map out the relocation.',
      'Our team coordinates packing zones, loading order, and transportation logistics.',
      'Furniture and equipment are delivered, unloaded, and placed according to your plan.',
      'We support a smooth reopening by helping your team settle quickly into the new space.',
    ],
    idealFor: [
      'Local businesses moving to a new premises',
      'Offices with desks, chairs, filing systems, and equipment to relocate',
      'Companies wanting an organised move with reduced operational downtime',
    ],
    seoPoints: [
      'Strong option for office removals, internal business moves, and commercial relocations in Wolverhampton.',
      'Designed to minimise downtime by planning around access, staff schedules, and key equipment handling.',
      'Suitable for desks, filing systems, meeting room furniture, boxed archives, and IT equipment.',
      'Useful for businesses moving locally or expanding into a new West Midlands premises.',
    ],
    faqs: [
      {
        question: 'Can VIP House Movers complete office removals outside normal business hours?',
        answer: 'Yes, where availability allows we can help businesses plan relocations around quieter periods so disruption to staff and customers is kept to a minimum.',
      },
      {
        question: 'Do you move office furniture and equipment as part of the service?',
        answer: 'Yes. We can move desks, chairs, filing cabinets, meeting room furniture, packed documents, and general office equipment with careful planning and handling.',
      },
    ],
  },
  {
    path: '/services/man-and-van',
    navLabel: 'Man and Van',
    cardTitle: 'Man and Van',
    title: 'Reliable Man and Van Services',
    description: 'Flexible man and van support from VIP House Movers for smaller jobs, quick collections, and cost-effective transport.',
    intro: 'Our man and van service is ideal when you need professional moving help without booking a full removal team, giving you the right level of support for everyday transport needs.',
    highlights: [
      'Perfect for small home moves, furniture collections, and single-item transport',
      'Professional driver and moving support to help with loading and unloading',
      'Fast, flexible bookings for local jobs and short-notice requests',
      'A practical solution when you want reliable help without overpaying',
    ],
    process: [
      'Tell us what needs moving, where it is going, and whether access is straightforward or restricted.',
      'We confirm the van size, labour needed, and best booking window for your move.',
      'Your items are loaded securely, transported carefully, and delivered on time.',
      'We can assist with room placement and heavier lifting where required.',
    ],
    idealFor: [
      'Single items, sofas, beds, and marketplace furniture collections',
      'Students, renters, and small households',
      'Customers needing affordable moving help with a professional touch',
    ],
    seoPoints: [
      'A practical choice for small moves, gumtree or marketplace collections, and one-off furniture transport.',
      'Popular with flat tenants, students, landlords, and customers who do not need a full removal crew.',
      'Works well for local transport jobs across Wolverhampton, Dudley, Walsall, and Birmingham.',
      'Flexible booking windows make it useful for short-notice collections and smaller home moves.',
    ],
    faqs: [
      {
        question: 'Is your man and van service suitable for furniture collections?',
        answer: 'Yes. It is ideal for collecting sofas, beds, tables, wardrobes, and other larger items from shops, sellers, storage units, or private addresses.',
      },
      {
        question: 'Can I book a man and van for a small flat move?',
        answer: 'Yes. If your move is smaller in scale, our man and van service can be a very cost-effective way to get professional help with loading, transport, and unloading.',
      },
    ],
  },
  {
    path: '/services/packing-services',
    navLabel: 'Packing Services',
    cardTitle: 'Packing Services',
    title: 'Professional Packing Services',
    description: 'Take the stress out of moving day with expert packing services from VIP House Movers using high-quality, eco-conscious materials.',
    intro: 'Our packing service is designed to save you time, reduce breakage risk, and make unpacking easier by ensuring every box is prepared, protected, and labelled with care.',
    highlights: [
      'Full packing, partial packing, or fragile-only packing options available',
      'Strong boxes, wrapping materials, and protection for delicate items',
      'Eco-friendly packing methods used wherever possible',
      'A smart option for busy families, professionals, and larger households',
    ],
    process: [
      'We identify the rooms and items that need packing support before moving day.',
      'Our team arrives with quality materials and packs methodically to protect each item.',
      'Boxes are labelled clearly to support smooth unloading and easier unpacking later.',
      'Where requested, we can also help unpack and remove used packing materials.',
    ],
    idealFor: [
      'Customers with fragile items, artwork, or valuables',
      'Busy households that need to save time before moving day',
      'Anyone wanting a safer and more organised move',
    ],
    seoPoints: [
      'Perfect for homeowners who want a full packing service before moving day arrives.',
      'Helps reduce breakage risk for kitchenware, ornaments, artwork, mirrors, and delicate belongings.',
      'Useful for busy professionals, families, and older customers who want a more hands-off move.',
      'Can be combined with house removals, flat removals, office moves, and fragile item transport.',
    ],
    faqs: [
      {
        question: 'Do you offer full packing and partial packing options?',
        answer: 'Yes. VIP House Movers can provide a complete packing service for the whole property or help with selected rooms and fragile items only.',
      },
      {
        question: 'Are your packing materials suitable for breakable items?',
        answer: 'Yes. We use strong boxes and protective wrapping methods to help keep delicate and valuable items better protected during the move.',
      },
    ],
  },
  {
    path: '/services/furniture-assembly',
    navLabel: 'Furniture Assembly',
    cardTitle: 'Furniture Assembly',
    title: 'Furniture Assembly and Reassembly Support',
    description: 'VIP House Movers can dismantle and reassemble furniture carefully so your move is safer, faster, and more convenient.',
    intro: 'Large wardrobes, bed frames, dining tables, and office furniture often need to be taken apart to move safely, and our team can handle that process with care and confidence.',
    highlights: [
      'Support for dismantling bulky or awkward furniture before transport',
      'Careful reassembly at your new property to help you settle in faster',
      'Reduced risk of damage when moving large furniture through tight spaces',
      'Ideal add-on for full house moves, flat moves, and office removals',
    ],
    process: [
      'We identify the furniture pieces that need dismantling before loading begins.',
      'Components are carefully removed, protected, and kept organised during transport.',
      'At the destination, furniture is rebuilt in the correct room and position.',
      'Final checks are completed so everything is secure and ready to use.',
    ],
    idealFor: [
      'Families moving larger bedroom and living room furniture',
      'Customers with limited access such as stairs or narrow hallways',
      'Anyone wanting a more complete, hands-off moving service',
    ],
    seoPoints: [
      'Helpful for bulky beds, wardrobes, tables, shelving units, and office furniture that cannot travel assembled.',
      'Reduces the risk of wall, doorway, and furniture damage when access is tight or awkward.',
      'Ideal as an add-on service for larger house removals, apartment moves, and business relocations.',
      'Saves customers time and effort when settling into a new home or workspace.',
    ],
    faqs: [
      {
        question: 'Can you dismantle and reassemble beds and wardrobes?',
        answer: 'Yes. VIP House Movers can help with common furniture items such as beds, wardrobes, dining tables, and other larger pieces where safe moving requires dismantling first.',
      },
      {
        question: 'Is furniture assembly included with a removal booking?',
        answer: 'It can be included or added depending on the service level you need. We tailor the move around the support required at both collection and delivery.',
      },
    ],
  },
  {
    path: '/services/storage-solutions',
    navLabel: 'Storage Solutions',
    cardTitle: 'Storage Solutions',
    title: 'Secure Storage Solutions',
    description: 'VIP House Movers provides practical storage solutions for customers who need extra flexibility before, during, or after a move.',
    intro: 'If your moving dates do not line up perfectly or you simply need more space, we can help you bridge the gap with clean, secure storage options that fit your timeline.',
    highlights: [
      'Short-term and longer-term storage options available',
      'Helpful when completion dates change or a property is not ready',
      'Ideal for decluttering before viewings, renovations, or downsizing',
      'Combined transport and storage support from one trusted team',
    ],
    process: [
      'We discuss how much needs storing and how long you expect to need the space.',
      'Your items are collected, handled carefully, and prepared for storage.',
      'When you are ready, we coordinate redelivery to your home, office, or new property.',
      'We keep communication clear so your storage plan stays simple and flexible.',
    ],
    idealFor: [
      'Moves with delayed completion dates',
      'Homeowners renovating or staging their property for sale',
      'Customers needing temporary breathing space during a transition',
    ],
    seoPoints: [
      'Useful when exchange and completion dates do not line up cleanly during a home move.',
      'Ideal for decluttering before viewings, renovations, downsizing, and probate-related house clearances.',
      'Lets customers combine removals and storage with one trusted Wolverhampton moving company.',
      'Flexible storage support helps reduce pressure when move dates change unexpectedly.',
    ],
    faqs: [
      {
        question: 'Can I use storage if my new property is not ready yet?',
        answer: 'Yes. Storage is a practical option when completion dates change or your new property is not ready for immediate delivery.',
      },
      {
        question: 'Do you offer storage as part of a removals package?',
        answer: 'Yes. VIP House Movers can help coordinate transport into storage and later redelivery, making the overall move simpler to manage.',
      },
    ],
  },
  {
    path: '/services/long-distance-removals',
    navLabel: 'Long Distance Removals',
    cardTitle: 'Long Distance Removals',
    title: 'Long Distance Removals Across the UK',
    description: 'Move with confidence anywhere in the UK using VIP House Movers for organised, dependable long distance removals.',
    intro: 'Long distance moves need more than transport alone, so we focus on planning, timing, and communication to make larger relocations feel smooth and fully managed from start to finish.',
    highlights: [
      'Ideal for regional, cross-country, and relocation moves across the UK',
      'Detailed planning to support timing, loading order, and route efficiency',
      'Safe transport for furniture, boxes, fragile items, and household essentials',
      'A premium service for customers moving far from Wolverhampton',
    ],
    process: [
      'We review the move size, locations, timing, and any overnight or access considerations.',
      'Our team builds a move plan that supports safe loading and efficient transport.',
      'Belongings are moved securely with updates and clear communication throughout.',
      'Delivery and placement are completed carefully so you can settle into your new home sooner.',
    ],
    idealFor: [
      'Families relocating to another town, city, or region',
      'Customers who need a dependable removal company for a bigger move',
      'Anyone wanting a planned, less stressful long-distance relocation',
    ],
    seoPoints: [
      'Designed for customers moving from Wolverhampton to another county, region, or city in the UK.',
      'Works well for household relocations that need stronger planning around travel time and access.',
      'Popular with families, professionals, and businesses making major location changes.',
      'A smart option when you want one experienced company managing the full move from start to finish.',
    ],
    faqs: [
      {
        question: 'Do you carry out long distance removals anywhere in the UK?',
        answer: 'Yes. VIP House Movers supports relocations from the West Midlands to destinations across the UK, with planning tailored to distance, timing, and property access.',
      },
      {
        question: 'How do you make a long-distance move less stressful?',
        answer: 'We focus on planning, communication, careful loading, and efficient transport so the move feels more controlled and easier to manage from collection to delivery.',
      },
    ],
  },
  {
    path: '/services/same-day-moves',
    navLabel: 'Same Day Moves',
    cardTitle: 'Same Day Moves',
    title: 'Fast and Flexible Same Day Moves',
    description: 'VIP House Movers offers same day moving support when timing is tight and you need a professional team to respond quickly.',
    intro: 'Sometimes moves need to happen fast, and our same day service is built to deliver practical, organised help without compromising on professionalism or care.',
    highlights: [
      'Responsive support for urgent local moves and short-notice bookings',
      'Efficient loading and transport to keep your day moving smoothly',
      'Suitable for homes, flats, furniture collections, and quick relocations',
      'A dependable option when plans change unexpectedly',
    ],
    process: [
      'We confirm availability, your collection point, destination, and key moving details quickly.',
      'Our team prepares the right vehicle and labour based on what needs moving.',
      'Items are collected, loaded, and transported with care despite the short turnaround.',
      'We work efficiently to help you complete the move within the same day where possible.',
    ],
    idealFor: [
      'Customers who have been let down by another mover',
      'Short-notice flat or house moves',
      'People needing quick, professional transport support',
    ],
    seoPoints: [
      'Strong choice when a property move must happen fast and same-day support is essential.',
      'Suitable for urgent local removals, furniture transport, and short-notice flat moves.',
      'Helpful when tenancy dates change suddenly or another moving company cancels.',
      'Keeps the process professional and organised even under time pressure.',
    ],
    faqs: [
      {
        question: 'Can you help if I need to move on the same day?',
        answer: 'Yes, subject to availability. VIP House Movers can often assist with urgent same-day moves when timing is tight and professional support is needed quickly.',
      },
      {
        question: 'What types of moves suit a same day service?',
        answer: 'Same day moves can work well for smaller house moves, flat relocations, urgent furniture transport, and situations where plans change unexpectedly.',
      },
    ],
  },
  {
    path: '/services/emergency-removals',
    navLabel: 'Emergency Removals',
    cardTitle: 'Emergency Removals',
    title: 'Emergency Removals When Time Matters Most',
    description: 'For urgent moving situations, VIP House Movers provides calm, practical emergency removal support when you need help quickly.',
    intro: 'Emergency removals require speed, reassurance, and a team that can adapt under pressure, and we work hard to deliver exactly that for customers dealing with unexpected circumstances.',
    highlights: [
      'Rapid support for urgent property changes and time-sensitive situations',
      'Professional movers who stay organised even when the timeline is tight',
      'Suitable for residential, rental, and last-minute relocation needs',
      'Clear communication to reduce stress during an already difficult situation',
    ],
    process: [
      'We take quick details about the urgency, property size, and items that need moving.',
      'The team assesses the most practical way to complete the move without delay.',
      'Your belongings are packed, loaded, and transported as efficiently as possible.',
      'We adapt to changing timings and keep you updated throughout the process.',
    ],
    idealFor: [
      'Last-minute landlord, tenancy, or property access changes',
      'Moves affected by delays, cancellations, or emergencies',
      'Customers who need a calm, capable team at short notice',
    ],
    seoPoints: [
      'Ideal when an urgent property situation calls for immediate professional moving help.',
      'Useful for tenancy issues, last-minute access problems, and unexpected relocation deadlines.',
      'Keeps customers supported by a calm, organised team during stressful circumstances.',
      'Provides a more dependable emergency option than trying to manage the move alone.',
    ],
    faqs: [
      {
        question: 'What counts as an emergency removal?',
        answer: 'Emergency removals are typically urgent moves caused by unexpected tenancy issues, cancelled movers, short-notice access changes, or other time-sensitive property situations.',
      },
      {
        question: 'Can you help at short notice if another company lets me down?',
        answer: 'Yes, where availability allows. VIP House Movers regularly helps customers who suddenly need a reliable team to step in and complete the move quickly.',
      },
    ],
  },
  {
    path: '/services/student-moves',
    navLabel: 'Student Moves',
    cardTitle: 'Student Moves',
    title: 'Affordable and Easy Student Moves',
    description: 'VIP House Movers helps students move between halls, flats, shared houses, and family homes with a service that is simple, practical, and affordable.',
    intro: 'Student moves often involve tight schedules, smaller loads, and multiple stops, so we keep the process straightforward and flexible while still delivering the same VIP level of care.',
    highlights: [
      'Perfect for university moves, end-of-term relocations, and shared housing',
      'Support for boxes, suitcases, desks, bedding, and small furniture',
      'Flexible bookings for short-distance and local moves',
      'A practical solution for students and parents alike',
    ],
    process: [
      'We confirm the collection point, destination, and amount being moved.',
      'Your belongings are loaded efficiently and protected during transport.',
      'We help unload at halls, flats, or new accommodation with clear room placement.',
      'The move is kept simple and cost-conscious without sacrificing professionalism.',
    ],
    idealFor: [
      'Students moving into or out of university accommodation',
      'Parents arranging transport for student belongings',
      'Shared house moves with smaller furniture and boxes',
    ],
    seoPoints: [
      'Perfect for end-of-term moves, new accommodation changes, and student house relocations.',
      'Works well for boxes, luggage, desks, chairs, bedding, and compact furniture loads.',
      'A practical and affordable service for students in Wolverhampton and nearby university areas.',
      'Flexible enough for halls, shared houses, studio flats, and family home collections.',
    ],
    faqs: [
      {
        question: 'Do you help students move between halls and private accommodation?',
        answer: 'Yes. VIP House Movers supports students moving between halls, shared houses, flats, and family homes with a simple and affordable service.',
      },
      {
        question: 'Is the student move service suitable for smaller loads?',
        answer: 'Yes. It is designed for student-style moves where the load is often lighter than a full house removal but still needs professional help and transport.',
      },
    ],
  },
  {
    path: '/services/flat-removals',
    navLabel: 'Flat Removals',
    cardTitle: 'Flat Removals',
    title: 'Professional Flat Removals',
    description: 'VIP House Movers makes flat removals easier with smart planning, careful handling, and support for restricted access or multi-storey buildings.',
    intro: 'Flat moves can be more demanding because of stairs, lifts, parking restrictions, and tighter layouts, which is why our team plans ahead to keep everything efficient and protected.',
    highlights: [
      'Support for ground-floor, upper-floor, and multi-storey flat moves',
      'Careful handling in communal spaces, lifts, and narrow stairwells',
      'Practical loading plans to reduce delays and unnecessary lifting',
      'Ideal for renters, first-time buyers, and downsizers',
    ],
    process: [
      'We assess access details such as floors, parking, entry systems, and lift availability.',
      'Our team prepares the move around building restrictions and loading requirements.',
      'Items are protected and moved carefully through shared spaces and tighter routes.',
      'We unload and position your belongings so settling in feels easier from the start.',
    ],
    idealFor: [
      'Tenants moving between flats or apartments',
      'Customers dealing with stairs, limited access, or tight parking',
      'Smaller homes that still need professional moving help',
    ],
    seoPoints: [
      'Well suited to flat removals where stairs, communal entrances, or lift access can slow a move down.',
      'Helps renters and first-time buyers move more efficiently in compact properties.',
      'A smart option for urban locations with parking restrictions and narrow hallways.',
      'Provides professional loading and transport even when access is awkward or time-limited.',
    ],
    faqs: [
      {
        question: 'Can you handle flat removals with stairs or limited lift access?',
        answer: 'Yes. VIP House Movers plans around stairs, lifts, communal entrances, and restricted access so the move can be handled as efficiently and safely as possible.',
      },
      {
        question: 'Are flat removals different from house removals?',
        answer: 'Often yes. Flat moves usually involve tighter spaces, shared access routes, and more parking or entry restrictions, which is why good planning is especially important.',
      },
    ],
  },
  {
    path: '/services/apartment-moves',
    navLabel: 'Apartment Moves',
    cardTitle: 'Apartment Moves',
    title: 'Apartment Moves with Premium Care',
    description: 'Move in or out of apartments smoothly with VIP House Movers and a team experienced in access planning, careful handling, and efficient transport.',
    intro: 'Apartment moves often involve concierge systems, loading bays, lifts, and timed access windows, and we help coordinate those details so your move feels controlled and stress-free.',
    highlights: [
      'Suitable for city apartments, managed buildings, and modern developments',
      'Support for building access rules, lift bookings, and loading areas',
      'Careful protection for furniture, décor, and boxed belongings',
      'A polished service that reflects the premium VIP House Movers brand',
    ],
    process: [
      'We gather building access information and timing requirements before move day.',
      'Our team arrives prepared for the layout, loading routes, and transport needs.',
      'Belongings are moved methodically and delivered with room-by-room care.',
      'We help ensure your apartment move feels smooth, tidy, and well organised.',
    ],
    idealFor: [
      'Apartment residents in managed developments',
      'Moves with concierge, lift, or loading bay requirements',
      'Customers wanting a premium and organised relocation experience',
    ],
    seoPoints: [
      'Ideal for apartment moves involving concierge desks, timed lift access, and loading bay rules.',
      'Popular with city centre customers who want a more polished and organised move.',
      'Reduces stress by planning around managed building procedures before move day begins.',
      'Suitable for premium residential developments across Birmingham and the wider West Midlands.',
    ],
    faqs: [
      {
        question: 'Can you work around apartment building access rules?',
        answer: 'Yes. VIP House Movers can plan around concierge systems, booked lifts, loading bays, and timed access windows to help the move run smoothly.',
      },
      {
        question: 'Is this service right for city centre apartment moves?',
        answer: 'Yes. It is particularly useful for city apartments where parking, lift access, and property management rules need to be considered in advance.',
      },
    ],
  },
  {
    path: '/services/house-clearance',
    navLabel: 'House Clearance',
    cardTitle: 'House Clearance',
    title: 'House Clearance Services',
    description: 'VIP House Movers provides respectful, efficient house clearance services for properties that need to be emptied quickly and carefully.',
    intro: 'Whether you are preparing a property for sale, managing a rental, or dealing with a sensitive family situation, we approach house clearances with professionalism, care, and practical support.',
    highlights: [
      'Clearance support for homes, rented properties, and inherited houses',
      'Efficient handling of furniture, appliances, boxes, and general contents',
      'Respectful service delivered with sensitivity and professionalism',
      'Helpful for preparing properties for sale, let, or renovation',
    ],
    process: [
      'We discuss the property size, access, and the level of clearance support required.',
      'Our team works through the property methodically to remove agreed contents safely.',
      'Items are loaded efficiently and handled responsibly during the clearance process.',
      'We help leave the property clear and ready for the next stage.',
    ],
    idealFor: [
      'Landlords, homeowners, and families managing property transitions',
      'Properties being sold, emptied, or prepared for refurbishment',
      'Customers wanting a dependable and respectful clearance service',
    ],
    seoPoints: [
      'Useful for probate situations, landlord clear-outs, pre-sale preparation, and renovation projects.',
      'Helps empty properties efficiently while keeping the process respectful and well managed.',
      'Suitable for clearing furniture, appliances, general household contents, and unwanted items.',
      'A valuable service when you need a property made ready for sale, let, or handover.',
    ],
    faqs: [
      {
        question: 'Do you carry out respectful house clearances for sensitive situations?',
        answer: 'Yes. VIP House Movers approaches house clearances professionally and respectfully, especially when the property change involves a difficult family or probate situation.',
      },
      {
        question: 'Can house clearance be combined with moving or storage support?',
        answer: 'Yes. Depending on your needs, we can help clear selected contents, move wanted items, and support storage arrangements as part of the overall job.',
      },
    ],
  },
  {
    path: '/services/piano-removals',
    navLabel: 'Piano Removals',
    cardTitle: 'Piano Removals',
    title: 'Specialist Piano Removals',
    description: 'Trust VIP House Movers for careful piano removals handled with planning, protection, and the respect valuable instruments deserve.',
    intro: 'Pianos are heavy, delicate, and often awkward to move, so we approach each piano removal with the extra care, handling techniques, and preparation needed for safe transport.',
    highlights: [
      'Careful moving support for upright pianos and similar large instruments',
      'Protection for floors, walls, and the instrument during transport',
      'Thoughtful planning for stairs, access points, and loading challenges',
      'Ideal for home moves, collections, and specialist transport needs',
    ],
    process: [
      'We assess the piano type, property layout, and access constraints before moving day.',
      'The instrument is protected and prepared using safe handling techniques.',
      'Our team loads and transports the piano carefully to reduce movement and risk.',
      'Delivery is completed with attention to placement and safe positioning at the destination.',
    ],
    idealFor: [
      'Homeowners moving upright pianos',
      'Families relocating valuable musical instruments',
      'Customers needing careful handling for heavy specialist items',
    ],
    seoPoints: [
      'Specialist support for piano transport where extra care and planning are essential.',
      'Helpful for upright piano moves involving stairs, awkward access, or delicate flooring.',
      'A safer option than attempting to move a heavy instrument without professional help.',
      'Ideal for home moves, single-item collections, and specialist transport jobs.',
    ],
    faqs: [
      {
        question: 'Can VIP House Movers move upright pianos safely?',
        answer: 'Yes. We approach piano removals with extra planning, protective handling, and careful transport to help reduce risk during the move.',
      },
      {
        question: 'Do piano removals need specialist planning?',
        answer: 'Yes. Pianos are heavy, delicate, and often awkward to manoeuvre, so access, flooring, stairs, and loading conditions all need to be considered properly beforehand.',
      },
    ],
  },
  {
    path: '/services/fragile-item-moving',
    navLabel: 'Fragile Item Moving',
    cardTitle: 'Fragile Item Moving',
    title: 'Fragile Item Moving with Extra Protection',
    description: 'VIP House Movers provides specialist support for fragile item moving so valuable, sentimental, and delicate belongings are handled with extra care.',
    intro: 'From artwork and mirrors to glassware, ornaments, and sentimental keepsakes, fragile items need a more thoughtful moving approach, and our team is here to provide it.',
    highlights: [
      'Extra protection for delicate, breakable, or sentimental belongings',
      'Suitable for artwork, mirrors, glassware, ornaments, and display pieces',
      'Professional wrapping, careful loading, and controlled transport methods',
      'Can be booked as part of a full move or as a standalone specialist service',
    ],
    process: [
      'We identify the fragile items that need specialist packing and moving support.',
      'Protective materials are used to wrap and secure each item properly.',
      'Items are loaded with placement in mind to minimise movement during transport.',
      'At delivery, we unload carefully and place delicate items where instructed.',
    ],
    idealFor: [
      'Customers with valuable, sentimental, or breakable belongings',
      'Households moving artwork, collectibles, or delicate décor',
      'Anyone wanting extra reassurance for fragile possessions',
    ],
    seoPoints: [
      'Designed for delicate items such as mirrors, framed artwork, ornaments, collectibles, and glassware.',
      'Adds reassurance for customers moving sentimental or high-value possessions.',
      'Useful as a standalone service or as part of a full house removal package.',
      'Focused on careful wrapping, safer loading positions, and more controlled transport.',
    ],
    faqs: [
      {
        question: 'Can you move fragile items on their own without a full house move?',
        answer: 'Yes. VIP House Movers can provide fragile item moving as a standalone specialist service or as part of a larger house or office removal.',
      },
      {
        question: 'What kinds of fragile belongings do you usually handle?',
        answer: 'We regularly help with mirrors, artwork, glassware, ornaments, display pieces, and other delicate or sentimental items that need extra protection in transit.',
      },
    ],
  },
];

export const servicePages = servicePageConfigs.map((config) => ({
  ...config,
  component: createServicePage(config),
}));
